import { useEffect, useState } from 'react'
import { useTranslation } from "react-i18next"
import mixpanel from 'mixpanel-browser'

import RecipeTags from '../components/RecipeTags'
import TagButton from '../components/TagButton'
import RecipeSummaryView from '../components/RecipeSummaryView'
import { findRandomRecipesWithTags, findRecipe } from '../util/api'
import {
    loadSelectionFilter,
    saveSelectionFilter,
    loadSelectionResult,
    saveSelectionResult,
} from '../util/storage'

import './SelectionFilter.css'

const courseOptions = [
    { value: 'breakfast', label: 'breakfast' },
    { value: 'main_course', label: 'main_course' },
    { value: 'soup', label: 'soup' },
    { value: 'dessert', label: 'dessert' },
    { value: 'snack', label: 'snack' },
]

const passesUnwantedTagsFilter = (recipe, unwantedTags) => {
    if (!unwantedTags.length || !recipe.tags || !recipe.tags.length) {
        return true
    }
    return !recipe.tags.some(tag => unwantedTags.includes(tag))
}

export default function SelectionFilter () {
    const { t } = useTranslation()

    const savedFilter = loadSelectionFilter()

    const [course, setCourse] = useState(savedFilter.course)
    const [wantedTags, setWantedTags] = useState(savedFilter.wantedTags)
    const [unwantedTags, setUnwantedTags] = useState(savedFilter.unwantedTags)
    const [filterOpen, setFilterOpen] = useState(false)
    const [recipes, setRecipes] = useState(null)

    const fetchRecipes = (nextCourse, nextWantedTags, nextUnwantedTags) => {
        findRandomRecipesWithTags([], nextCourse, nextWantedTags).then((result) => {
            const filtered = (result || []).filter(recipe => passesUnwantedTagsFilter(recipe, nextUnwantedTags))
            setRecipes(filtered)
            saveSelectionResult(filtered.map(recipe => recipe.id))
        })
    }

    useEffect(() => {
        const savedResultIds = loadSelectionResult()

        if (savedResultIds.length) {
            Promise.all(savedResultIds.map(id => findRecipe(id))).then((loadedRecipes) => {
                const validRecipes = loadedRecipes.filter(recipe => recipe && recipe.id)
                if (validRecipes.length) {
                    setRecipes(validRecipes)
                    return
                }
                fetchRecipes(course, wantedTags, unwantedTags)
            })
        } else {
            fetchRecipes(course, wantedTags, unwantedTags)
        }
    }, [])

    useEffect(() => {
        if (!filterOpen) {
            return
        }
        const scrollContainer = document.querySelector('.main-block') || window
        const closeOnScroll = () => setFilterOpen(false)
        scrollContainer.addEventListener('scroll', closeOnScroll, { passive: true })
        return () => scrollContainer.removeEventListener('scroll', closeOnScroll)
    }, [filterOpen])

    const updateFilter = (nextCourse, nextWantedTags, nextUnwantedTags) => {
        saveSelectionFilter({ course: nextCourse, wantedTags: nextWantedTags, unwantedTags: nextUnwantedTags })
        fetchRecipes(nextCourse, nextWantedTags, nextUnwantedTags)
    }

    const selectCourse = (value) => {
        mixpanel.track(`Filter-${value}`)
        setCourse(value)
        updateFilter(value, wantedTags, unwantedTags)
    }

    const updateWantedTags = (nextWantedTags) => {
        setWantedTags(nextWantedTags)
        updateFilter(course, nextWantedTags, unwantedTags)
    }

    const updateUnwantedTags = (nextUnwantedTags) => {
        setUnwantedTags(nextUnwantedTags)
        updateFilter(course, wantedTags, nextUnwantedTags)
    }

    return (
        <div className={`selection-filter${filterOpen ? ' open' : ''}`}>
            <div className={`selection-filter-panel${filterOpen ? ' open' : ''}`}>
                {!filterOpen &&
                    <div className="selection-filter-toggle" onClick={() => setFilterOpen(true)}>
                        <span>{t('Filter')}</span>
                        <span className="selection-filter-chevron">&#9660;</span>
                    </div>
                }
                <div className="selection-filter-content">
                    <div className="selection-filter-section">
                        <div className="selection-filter-section-options selection-filter-course-options" role="radiogroup" aria-label={t('Filter')}>
                            {courseOptions.map(option => (
                                <TagButton
                                    key={option.value}
                                    value={option.value}
                                    label={t(option.label)}
                                    selected={course === option.value}
                                    onToggle={() => selectCourse(option.value)}
                                    role="radio"
                                />
                            ))}
                        </div>
                    </div>
                    <RecipeTags
                        wantedTags={wantedTags}
                        setWantedTags={updateWantedTags}
                        unwantedTags={unwantedTags}
                        setUnwantedTags={updateUnwantedTags}
                    />
                </div>
                {filterOpen &&
                    <div className="selection-filter-toggle selection-filter-toggle--bottom" onClick={() => setFilterOpen(false)}>
                        <span>{t('Filter')}</span>
                        <span className="selection-filter-chevron open">&#9660;</span>
                    </div>
                }
            </div>

            <div className="selection-recipes">
                {recipes && !recipes.length &&
                    <div className="selection-empty">{t("Can't find any recipes, try different tags or type.")}</div>
                }
                {recipes && recipes.map(recipe =>
                    <RecipeSummaryView recipe={recipe} mixpanelEvent="Selection" key={recipe.id} />
                )}
            </div>
        </div>
    )
}
